<script setup lang="ts">
import { ref } from 'vue';
import { Check, Copy } from 'lucide-vue-next';
import { copyText } from '@/utils/nativeShell';

/**
 * A small copy button beside a value. It shows when the field's row is
 * hovered or tabbed into, and always on a touch screen, which has no hover.
 * After a copy it shows a check for a moment.
 */
const props = defineProps<{ text: string; label: string }>();

const copied = ref(false);
let resetTimer: ReturnType<typeof setTimeout> | undefined;

async function copy(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    copied.value = await copyText(props.text);
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => (copied.value = false), 1500);
}
</script>

<template>
  <button
    type="button"
    data-testid="copy-value"
    :title="copied ? 'Copied' : 'Copy'"
    :aria-label="copied ? `Copied ${label}` : `Copy ${label}`"
    class="inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity group-hover/field:opacity-100 group-focus-within/field:opacity-100 hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none [@media(hover:none)]:opacity-100"
    @click="copy"
  >
    <Check v-if="copied" class="size-3.5" aria-hidden="true" />
    <Copy v-else class="size-3.5" aria-hidden="true" />
  </button>
</template>
